import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setHapticsEnabled, setLanguage } from '@/store/settings.slice';
import { setTourActive } from '@/store/core.slice';

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'it', label: 'Italiano' },
] as const;

/**
 * Preferences panel — language, haptics, replay tutorial. Reached from the
 * Profilo hub's "— Impostazioni —" block. (Split out of the old Settings.tsx.)
 */
export function Preferences() {
  const { t, i18n } = useTranslation('general');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const settings = useAppSelector((s) => s.settings);

  const handleLanguage = (code: string) => {
    dispatch(setLanguage(code));
    void i18n.changeLanguage(code);
  };

  return (
    <div className="flex flex-col gap-6 px-4 pb-6 pt-5">
      <header>
        <h1 className="text-2xl font-bold text-fg">
          {t('preferences', { defaultValue: 'Preferences' })}
        </h1>
      </header>

      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
          {t('language', { defaultValue: 'Language' })}
        </h2>
        <div className="card-edge flex flex-col gap-1 overflow-hidden rounded-xl bg-bg-secondary">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => handleLanguage(lang.code)}
              className="flex items-center justify-between px-3 py-3 text-left transition-opacity active:opacity-80"
            >
              <span className="text-sm text-fg">{lang.label}</span>
              {settings.language === lang.code && (
                <span className="text-xs font-medium text-brand">
                  {t('active', { defaultValue: 'Active' })}
                </span>
              )}
            </button>
          ))}
        </div>
      </section>

      <section className="flex flex-col gap-2">
        <label className="card-edge flex items-center justify-between rounded-xl bg-bg-secondary p-3">
          <span className="text-sm text-fg">
            {t('haptics', { defaultValue: 'Haptic feedback' })}
          </span>
          <input
            type="checkbox"
            className="accent-brand"
            checked={settings.hapticsEnabled}
            onChange={(e) => dispatch(setHapticsEnabled(e.target.checked))}
          />
        </label>
        <button
          type="button"
          onClick={() => {
            dispatch(setTourActive(true));
            navigate('/');
          }}
          className="card-edge rounded-xl bg-bg-secondary p-3 text-left text-sm font-medium text-fg transition-opacity active:opacity-80"
        >
          {t('settings.replayTutorial', { ns: 'onboarding', defaultValue: 'Replay the tutorial' })}
        </button>
      </section>
    </div>
  );
}
